import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  Phone, Users, Image, FileText, Plus, Trash2, Edit, ArrowLeft, 
  Video, Eye, Copy, Check, LogOut
} from 'lucide-react';

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/88797887-5fbe-4b8e-86d9-2a8b6e9a51f8/images/24255a550df67aeec175ac877a674e8bae385938fef42044bf92b3a8217b6427.png";
const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPanel() {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  
  // State
  const [clients, setClients] = useState([]);
  const [media, setMedia] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Client form
  const [clientForm, setClientForm] = useState({ name: '', email: '', password: '' });
  const [editingClient, setEditingClient] = useState(null);
  const [showClientDialog, setShowClientDialog] = useState(false);
  
  // Media form
  const [mediaForm, setMediaForm] = useState({ url: '', title: '', description: '', media_type: 'image', category: '' });
  const [showMediaDialog, setShowMediaDialog] = useState(false);
  
  // Copied state
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    if (!loading && (!user || user.role !== 'admin')) {
      navigate('/login');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user && user.role === 'admin') {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    setLoadingData(true);
    try {
      const [clientsRes, mediaRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/clients`, { withCredentials: true }),
        axios.get(`${API_URL}/api/media`, { withCredentials: true })
      ]);
      setClients(clientsRes.data);
      setMedia(mediaRes.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      setError('Failed to load data');
    } finally {
      setLoadingData(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // ============ CLIENT MANAGEMENT ============
  
  const handleCreateClient = async () => {
    setError('');
    setSuccess('');
    try {
      await axios.post(`${API_URL}/api/admin/clients`, clientForm, { withCredentials: true });
      setSuccess('Client created successfully');
      setClientForm({ name: '', email: '', password: '' });
      setShowClientDialog(false);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create client');
    }
  };

  const handleUpdateClient = async () => {
    setError('');
    setSuccess('');
    try {
      const updateData = {};
      if (clientForm.name) updateData.name = clientForm.name;
      if (clientForm.email) updateData.email = clientForm.email;
      if (clientForm.password) updateData.password = clientForm.password;
      
      await axios.put(`${API_URL}/api/admin/clients/${editingClient.id}`, updateData, { withCredentials: true });
      setSuccess('Client updated successfully');
      setEditingClient(null);
      setClientForm({ name: '', email: '', password: '' });
      setShowClientDialog(false);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update client');
    }
  };

  const handleDeleteClient = async (clientId) => {
    if (!window.confirm('Are you sure you want to delete this client?')) return;
    setError('');
    try {
      await axios.delete(`${API_URL}/api/admin/clients/${clientId}`, { withCredentials: true });
      setSuccess('Client deleted successfully');
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete client');
    }
  };

  const openEditClient = (client) => {
    setEditingClient(client);
    setClientForm({ name: client.name, email: client.email, password: '' });
    setShowClientDialog(true);
  };

  const openNewClient = () => {
    setEditingClient(null);
    setClientForm({ name: '', email: '', password: '' });
    setShowClientDialog(true);
  };

  // ============ MEDIA MANAGEMENT ============
  
  const handleCreateMedia = async () => {
    setError('');
    setSuccess('');
    try {
      await axios.post(`${API_URL}/api/admin/media`, mediaForm, { withCredentials: true });
      setSuccess('Media added successfully');
      setMediaForm({ url: '', title: '', description: '', media_type: 'image', category: '' });
      setShowMediaDialog(false);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add media');
    }
  };

  const handleDeleteMedia = async (mediaId) => {
    if (!window.confirm('Are you sure you want to delete this media?')) return;
    setError('');
    try {
      await axios.delete(`${API_URL}/api/admin/media/${mediaId}`, { withCredentials: true });
      setSuccess('Media deleted successfully');
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete media');
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (loading || loadingData) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F172A] mx-auto"></div>
          <p className="font-jakarta text-[#475569] mt-4">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user || user.role !== 'admin') {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-[#0F172A] text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-3" data-testid="admin-logo">
                <img src={LOGO_URL} alt="Essex Street Contracting" className="h-10 w-auto" />
                <div className="hidden sm:block">
                  <p className="font-outfit font-bold text-white text-sm leading-tight">Admin Panel</p>
                  <p className="font-outfit text-[#D4AF37] text-xs leading-tight">Essex Street Contracting</p>
                </div>
              </Link>
            </div>

            <div className="flex items-center gap-4">
              <Link to="/dashboard">
                <Button variant="outline" className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0F172A] rounded-sm" data-testid="back-dashboard-btn">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Dashboard
                </Button>
              </Link>
              <Button 
                onClick={handleLogout}
                variant="ghost"
                className="text-gray-300 hover:text-white"
                data-testid="admin-logout-btn"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Alerts */}
        {error && (
          <Alert variant="destructive" className="mb-6" data-testid="admin-error">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {success && (
          <Alert className="mb-6 border-green-500 bg-green-50" data-testid="admin-success">
            <AlertDescription className="text-green-800">{success}</AlertDescription>
          </Alert>
        )}

        <Tabs defaultValue="clients" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-8 bg-white border border-[#E2E8F0]">
            <TabsTrigger 
              value="clients" 
              className="font-jakarta data-[state=active]:bg-[#0F172A] data-[state=active]:text-white rounded-sm"
              data-testid="clients-tab"
            >
              <Users className="h-4 w-4 mr-2" />
              Client Management
            </TabsTrigger>
            <TabsTrigger 
              value="media" 
              className="font-jakarta data-[state=active]:bg-[#0F172A] data-[state=active]:text-white rounded-sm"
              data-testid="media-tab"
            >
              <Image className="h-4 w-4 mr-2" />
              Media Gallery
            </TabsTrigger>
          </TabsList>

          {/* ============ CLIENTS TAB ============ */}
          <TabsContent value="clients">
            <div className="bg-white border border-[#E2E8F0] rounded-sm">
              <div className="p-6 border-b border-[#E2E8F0] flex items-center justify-between">
                <div>
                  <h2 className="font-outfit text-xl font-semibold text-[#0F172A]">Client Accounts</h2>
                  <p className="font-jakarta text-sm text-[#475569]">Manage client login credentials</p>
                </div>
                <Dialog open={showClientDialog} onOpenChange={setShowClientDialog}>
                  <DialogTrigger asChild>
                    <Button 
                      onClick={openNewClient}
                      className="bg-[#D4AF37] text-[#0F172A] hover:bg-[#0F172A] hover:text-white rounded-sm font-jakarta"
                      data-testid="add-client-btn"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Client
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle className="font-outfit">
                        {editingClient ? 'Edit Client' : 'Add New Client'}
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label className="font-jakarta">Full Name</Label>
                        <Input
                          value={clientForm.name}
                          onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })}
                          placeholder="John Smith"
                          className="rounded-sm"
                          data-testid="client-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="font-jakarta">Email Address</Label>
                        <Input
                          type="email"
                          value={clientForm.email}
                          onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })}
                          placeholder="client@email.com"
                          className="rounded-sm"
                          data-testid="client-email-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="font-jakarta">
                          Password {editingClient && <span className="text-[#475569] text-xs">(leave blank to keep current)</span>}
                        </Label>
                        <Input
                          type="password"
                          value={clientForm.password}
                          onChange={(e) => setClientForm({ ...clientForm, password: e.target.value })}
                          placeholder="••••••••"
                          className="rounded-sm"
                          data-testid="client-password-input"
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <DialogClose asChild>
                        <Button variant="outline" className="rounded-sm">Cancel</Button>
                      </DialogClose>
                      <Button 
                        onClick={editingClient ? handleUpdateClient : handleCreateClient}
                        className="bg-[#0F172A] text-white hover:bg-[#D4AF37] hover:text-[#0F172A] rounded-sm"
                        data-testid="save-client-btn"
                      >
                        {editingClient ? 'Update Client' : 'Create Client'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>

              {clients.length === 0 ? (
                <div className="p-8 text-center" data-testid="no-clients-message">
                  <Users className="h-12 w-12 text-[#94A3B8] mx-auto mb-4" />
                  <h3 className="font-outfit text-lg font-semibold text-[#0F172A]">No Clients Yet</h3>
                  <p className="font-jakarta text-[#475569] mt-2">
                    Add your first client to get started.
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-[#E2E8F0]">
                  {clients.map((client) => (
                    <div key={client.id} className="p-4 sm:p-6 flex items-center justify-between hover:bg-[#F8FAFC]" data-testid={`client-row-${client.id}`}>
                      <div>
                        <h3 className="font-outfit font-semibold text-[#0F172A]">{client.name}</h3>
                        <p className="font-jakarta text-sm text-[#475569]">{client.email}</p>
                        <p className="font-mono text-xs text-[#94A3B8] mt-1">Created: {new Date(client.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => openEditClient(client)}
                          className="rounded-sm"
                          data-testid={`edit-client-${client.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleDeleteClient(client.id)}
                          className="rounded-sm text-red-600 hover:bg-red-50"
                          data-testid={`delete-client-${client.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* ============ MEDIA TAB ============ */}
          <TabsContent value="media">
            <div className="bg-white border border-[#E2E8F0] rounded-sm">
              <div className="p-6 border-b border-[#E2E8F0] flex items-center justify-between">
                <div>
                  <h2 className="font-outfit text-xl font-semibold text-[#0F172A]">Project Gallery</h2>
                  <p className="font-jakarta text-sm text-[#475569]">Add photos and videos of your work</p>
                </div>
                <Dialog open={showMediaDialog} onOpenChange={setShowMediaDialog}>
                  <DialogTrigger asChild>
                    <Button 
                      className="bg-[#D4AF37] text-[#0F172A] hover:bg-[#0F172A] hover:text-white rounded-sm font-jakarta"
                      data-testid="add-media-btn"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Media
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                      <DialogTitle className="font-outfit">Add New Media</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label className="font-jakarta">Media Type</Label>
                        <div className="flex gap-4">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input 
                              type="radio" 
                              name="media_type" 
                              value="image"
                              checked={mediaForm.media_type === 'image'}
                              onChange={(e) => setMediaForm({ ...mediaForm, media_type: e.target.value })}
                              className="text-[#D4AF37]"
                            />
                            <Image className="h-4 w-4" /> Image
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input 
                              type="radio" 
                              name="media_type" 
                              value="video"
                              checked={mediaForm.media_type === 'video'}
                              onChange={(e) => setMediaForm({ ...mediaForm, media_type: e.target.value })}
                              className="text-[#D4AF37]"
                            />
                            <Video className="h-4 w-4" /> Video
                          </label>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="font-jakarta">URL</Label>
                        <Input
                          value={mediaForm.url}
                          onChange={(e) => setMediaForm({ ...mediaForm, url: e.target.value })}
                          placeholder={mediaForm.media_type === 'video' ? 'https://youtube.com/watch?v=...' : 'https://example.com/image.jpg'}
                          className="rounded-sm"
                          data-testid="media-url-input"
                        />
                        <p className="text-xs text-[#475569]">
                          {mediaForm.media_type === 'video' 
                            ? 'Paste a YouTube URL or direct video link (.mp4)'
                            : 'Paste a direct image URL (.jpg, .png, .webp)'}
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label className="font-jakarta">Title</Label>
                        <Input
                          value={mediaForm.title}
                          onChange={(e) => setMediaForm({ ...mediaForm, title: e.target.value })}
                          placeholder="Kitchen Restoration - Queens Project"
                          className="rounded-sm"
                          data-testid="media-title-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="font-jakarta">Category</Label>
                        <select
                          value={mediaForm.category}
                          onChange={(e) => setMediaForm({ ...mediaForm, category: e.target.value })}
                          className="w-full border border-[#E2E8F0] rounded-sm p-2 font-jakarta text-sm"
                          data-testid="media-category-select"
                        >
                          <option value="">Select category...</option>
                          <option value="kitchen">Kitchen</option>
                          <option value="bathroom">Bathroom</option>
                          <option value="basement">Basement</option>
                          <option value="water-damage">Water Damage</option>
                          <option value="electrical">Electrical</option>
                          <option value="plumbing">Plumbing</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label className="font-jakarta">Description (optional)</Label>
                        <Input
                          value={mediaForm.description}
                          onChange={(e) => setMediaForm({ ...mediaForm, description: e.target.value })}
                          placeholder="Brief description of the project"
                          className="rounded-sm"
                          data-testid="media-description-input"
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <DialogClose asChild>
                        <Button variant="outline" className="rounded-sm">Cancel</Button>
                      </DialogClose>
                      <Button 
                        onClick={handleCreateMedia}
                        className="bg-[#0F172A] text-white hover:bg-[#D4AF37] hover:text-[#0F172A] rounded-sm"
                        data-testid="save-media-btn"
                      >
                        Add Media
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>

              {media.length === 0 ? (
                <div className="p-8 text-center" data-testid="no-media-message">
                  <Image className="h-12 w-12 text-[#94A3B8] mx-auto mb-4" />
                  <h3 className="font-outfit text-lg font-semibold text-[#0F172A]">No Media Yet</h3>
                  <p className="font-jakarta text-[#475569] mt-2 mb-4">
                    Add photos and videos of your completed projects.
                  </p>
                  <div className="bg-[#F8FAFC] p-4 rounded-sm max-w-md mx-auto text-left">
                    <p className="font-jakarta text-sm text-[#475569] mb-2"><strong>How to add media:</strong></p>
                    <ol className="font-jakarta text-sm text-[#475569] space-y-1 list-decimal list-inside">
                      <li>Upload your image to a hosting service (Imgur, Google Photos, etc.)</li>
                      <li>Copy the direct image URL</li>
                      <li>Click "Add Media" and paste the URL</li>
                    </ol>
                  </div>
                </div>
              ) : (
                <div className="p-6">
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {media.map((item) => (
                      <div key={item.id} className="group relative border border-[#E2E8F0] rounded-sm overflow-hidden" data-testid={`media-item-${item.id}`}>
                        {item.media_type === 'video' ? (
                          <div className="aspect-square bg-[#0F172A] flex items-center justify-center">
                            <Video className="h-12 w-12 text-[#D4AF37]" />
                          </div>
                        ) : (
                          <img 
                            src={item.url} 
                            alt={item.title}
                            className="aspect-square w-full object-cover"
                            onError={(e) => {
                              e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23F1F5F9" width="100" height="100"/><text x="50" y="50" text-anchor="middle" dy=".3em" fill="%2394A3B8" font-size="12">Image Error</text></svg>';
                            }}
                          />
                        )}
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          <a href={item.url} target="_blank" rel="noopener noreferrer">
                            <Button size="sm" variant="secondary" className="rounded-sm">
                              <Eye className="h-4 w-4" />
                            </Button>
                          </a>
                          <Button 
                            size="sm" 
                            variant="secondary" 
                            className="rounded-sm"
                            onClick={() => copyToClipboard(item.url, item.id)}
                          >
                            {copiedId === item.id ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                          </Button>
                          <Button 
                            size="sm" 
                            variant="destructive" 
                            className="rounded-sm"
                            onClick={() => handleDeleteMedia(item.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="p-2 bg-white">
                          <p className="font-jakarta text-xs font-medium text-[#0F172A] truncate">{item.title}</p>
                          {item.category && (
                            <span className="inline-block mt-1 px-2 py-0.5 bg-[#F1F5F9] text-[#475569] text-xs rounded-sm capitalize">
                              {item.category}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Instructions Card */}
            <div className="mt-6 bg-[#0F172A] text-white p-6 rounded-sm">
              <h3 className="font-outfit text-lg font-semibold mb-4">How to Add Project Photos & Videos</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-jakarta font-semibold text-[#D4AF37] mb-2">For Images:</h4>
                  <ol className="font-jakarta text-sm text-gray-300 space-y-2 list-decimal list-inside">
                    <li>Take photos of your completed work</li>
                    <li>Upload to <a href="https://imgur.com" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] underline">Imgur</a>, Google Photos, or any image host</li>
                    <li>Right-click the image → "Copy image address"</li>
                    <li>Paste the URL in the form above</li>
                  </ol>
                </div>
                <div>
                  <h4 className="font-jakarta font-semibold text-[#D4AF37] mb-2">For Videos:</h4>
                  <ol className="font-jakarta text-sm text-gray-300 space-y-2 list-decimal list-inside">
                    <li>Upload your video to YouTube</li>
                    <li>Copy the video URL from the address bar</li>
                    <li>Select "Video" type and paste the URL</li>
                    <li>Videos will display as embedded players</li>
                  </ol>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
